import { useEffect, useState, type RefObject } from 'react';

export interface ScrollEdges {
  /** Content is hidden to the left of the viewport (the container is scrolled). */
  left: boolean;
  /** Content is hidden to the right of the viewport. */
  right: boolean;
}

const NONE: ScrollEdges = { left: false, right: false };

/**
 * Whether a scroll container is hiding content past its left or right edge.
 *
 * This exists because of VISUAL §4.2: we wrote down that we would freeze the
 * first column *and show the cut*, precisely because the product we take the
 * look from hides half its table without a word. Callers turn these two flags
 * into the shadow the pinned columns cast and the fade before the action rail.
 */
export function useScrollEdges(
  ref: RefObject<HTMLElement | null>,
  /** Anything that changes the content width: column count, row count, density. */
  contentKey: string | number,
): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>(NONE);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => {
      const max = node.scrollWidth - node.clientWidth;
      // One pixel of slack: sub-pixel layouts otherwise report a cut that is not
      // there and leave the shadow painted over a table with nothing hidden.
      const left = node.scrollLeft > 1;
      const right = node.scrollLeft < max - 1;
      // Same object unless the answer changed: a scroll event fires dozens of
      // times a second and each new object would re-render the whole table,
      // which is the 60fps budget of ARCHITECTURE §7 spent on two booleans.
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };

    let frame = 0;
    const schedule = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    measure();
    node.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(node);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      node.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, [ref, contentKey]);

  return edges;
}
